import { faPlus, faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import SectionHeading from '@ui/SectionHeading';
import { CheckAccess } from 'components/Clubs/ClubPermissionManagement/CheckAccess';
import Loader from 'components/Loader';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { MollieConnectPanel } from '../types';
import AccountCard from './AccountCard';
import ModuleAssignments from './ModuleAssignments';
import MollieConnectTutorial from './MollieConnectTutorial';

interface Props {
    clubSlug: string;
}

export const MollieConnectAssistant = ({ clubSlug }: Props) => {
    const { t } = useTranslation('general');
    const { data, error, mutate, isValidating } = useSWR<MollieConnectPanel>(`connect/${clubSlug}/panel`);
    // Ohne Verbindung antwortet das Backend mit connected: false; ein Lesefehler landet
    // hier ebenfalls ohne Konten, und in beiden Faellen ist das Tutorial richtig.
    const accounts = data?.accounts ?? [];

    return (
        <CheckAccess clubSlug={clubSlug} permission="club.mollie">
            <Loader isLoading={!data && !error}>
                {accounts.length === 0 ? (
                    <MollieConnectTutorial clubSlug={clubSlug} />
                ) : (
                    <div className="tw-space-y-6">
                        <div className="tw-space-y-3">
                            <SectionHeading title={t('mollie.assistant.accountsTitle')} />
                            {accounts.map((account) => (
                                <AccountCard
                                    key={account.id}
                                    account={account}
                                    canDisconnect={!account.is_default || accounts.length === 1}
                                    onChange={() => mutate()}
                                />
                            ))}
                            <div className="tw-flex tw-gap-2">
                                <a
                                    href={`${process.env.NEXT_PUBLIC_BACKEND_HOST}connect/${clubSlug}`}
                                    target="_blank"
                                    className="tw-flex-1"
                                >
                                    <Button
                                        block
                                        size="sm"
                                        color="secondary"
                                        flat
                                        icon={<FontAwesomeIcon icon={faPlus} />}
                                    >
                                        {t('mollie.assistant.addAccount')}
                                    </Button>
                                </a>
                                <Button
                                    size="sm"
                                    color="light"
                                    isLoading={isValidating}
                                    aria-label={t('mollie.assistant.refresh')}
                                    icon={<FontAwesomeIcon icon={faSync} />}
                                    onPress={() => mutate()}
                                />
                            </div>
                        </div>

                        {accounts.length > 1 && (
                            <ModuleAssignments
                                clubSlug={clubSlug}
                                panel={data}
                                onChange={(assignments) => mutate({ ...data, assignments }, { revalidate: false })}
                            />
                        )}
                    </div>
                )}
            </Loader>
        </CheckAccess>
    );
};

export default MollieConnectAssistant;
